import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PlusIcon, BanknotesIcon } from "@heroicons/react/24/outline";
import { useBuilding } from "../../context/BuildingContext";
import { bankAccountsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import EmptyState from "../../components/common/EmptyState";
import PageHeader from "../../components/common/PageHeader";
import { formatCurrency } from "../../utils/formatters";
import { BankAccount, BankAccountType } from "@akfdev-com/shared/src/models";

const accountTypeLabels: Record<BankAccountType, string> = {
  [BankAccountType.CurrentAccount]: "Current Account",
  [BankAccountType.SavingsAccount]: "Savings Account",
  [BankAccountType.CashAccount]: "Cash Account",
  [BankAccountType.CreditCard]: "Credit Card",
  [BankAccountType.TrustAccount]: "Trust Account",
  [BankAccountType.Other]: "Other",
};

const BankingList: React.FC = () => {
  const { currentBuilding } = useBuilding();
  const buildingId = currentBuilding?.id ?? "";

  const {
    data: accounts,
    isLoading,
    error,
  } = useQuery<BankAccount[]>({
    queryKey: ["bank-accounts", buildingId],
    queryFn: () => bankAccountsApi.getAll(buildingId),
    enabled: !!buildingId,
  });

  if (isLoading) return <Spinner />;
  if (error)
    return (
      <ErrorAlert message="Failed to load bank accounts. Please try again." />
    );

  return (
    <div>
      <PageHeader
        title="Banking"
        description="Manage bank accounts and transactions for this building."
        actions={
          <Link
            to="/dashboard/banking/new"
            className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 sm:py-2 border border-transparent text-base sm:text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 min-h-[44px] sm:min-h-0"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Add Account
          </Link>
        }
      />

      {!accounts || accounts.length === 0 ? (
        <EmptyState
          title="No bank accounts"
          description="Add a bank account to start tracking transactions."
          actionText="Add Account"
          actionHref="/dashboard/banking/new"
          icon={<BanknotesIcon className="h-12 w-12 text-gray-400" />}
        />
      ) : (
        <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {accounts.map((account) => (
            <Link
              key={account.id}
              to={`/dashboard/banking/${account.id}?buildingId=${buildingId}`}
              className="block bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-5"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 truncate">
                    {account.name}
                  </h3>
                  {account.bankName && (
                    <p className="text-sm text-gray-500 mt-0.5">
                      {account.bankName}
                    </p>
                  )}
                </div>
                <span
                  className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    account.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {account.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              <dl className="mt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">Type</dt>
                  <dd className="text-gray-900 font-medium">
                    {accountTypeLabels[account.accountType] ??
                      account.accountType}
                  </dd>
                </div>
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">Currency</dt>
                  <dd className="text-gray-900">{account.currency}</dd>
                </div>
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">Opening Balance</dt>
                  <dd className="text-gray-900 font-semibold">
                    {formatCurrency(
                      account.openingBalance / 100,
                      account.currency,
                    )}
                  </dd>
                </div>
              </dl>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default BankingList;
